import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowLeft, Banknote, CalendarCheck, CalendarDays, CircleCheckBig, CircleX, Clock, Download, Eye, Filter, Hourglass,
  Play, Printer, RotateCcw, Search, Users, Wallet,
} from 'lucide-react';
import * as hr from '../api';
import Loader from '../components/Loader';
import { DateInput, Select } from '../components/Controls';
import { download, fmtMoney, fmtNumber, t12, times12, toCSV } from '../format';
import { useFrozen } from './attendance/shared';
import { Chip, Kpi, PAY_TONE, STATUS_TONE, Table, YesNo, money, num } from './missed/shared';

// Missed Days Statement (Stephanet's MissedDaysStatement): one row per employee for the period, with a View that opens the employee's
// missed days history (details, leave offset, lateness, payroll impact, year-by-year entitlement, management actions)

const HR_TONE = { 'Pending HR Review': 'yellow', 'Partially Reviewed': 'orange', Closed: 'green' };
const FROZEN = 3;
const EMPTY = { start: '', end: '', q: '', department: '', branch: '', site: '', payroll_status: '', hr_status: '' };

export default function MissedDaysStatement() {
  const [draft, setDraft] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [quick, setQuick] = useState('');
  const [viewing, setViewing] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    hr.mdStatement(applied).then(d => { setData(d); setError(''); setTick(t => t + 1); }).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [applied]);

  const rows = useMemo(() => {
    if (!data) return [];
    const q = quick.trim().toLowerCase();
    return q ? data.rows.filter(r => [r.name, r.employee_no, r.department, r.designation, r.branch, r.site].some(v => String(v || '').toLowerCase().includes(q))) : data.rows;
  }, [data, quick]);
  const tableRef = useFrozen([data, rows.length, viewing], FROZEN);

  if (viewing) return <EmployeeHistory id={viewing} period={applied} onBack={() => setViewing(null)} />;
  if (error && !data) return <div className="mdr"><div className="mdr-card mdr-empty">{error}</div></div>;
  if (!data) return <Loader title="Loading missed days statement…" subtitle="Summarising each employee" />;

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const run = e => { e?.preventDefault(); setApplied({ ...draft }); };
  const clear = () => { setDraft(EMPTY); setApplied(EMPTY); setQuick(''); };
  const t = data.totals;
  const sum = k => rows.reduce((s, r) => s + (r[k] || 0), 0);
  const period = applied.start || applied.end ? `${applied.start || 'start'} to ${applied.end || 'date'}` : 'All dates';
  const exportCsv = () => download(`missed-days-statement${applied.start ? `-${applied.start}` : ''}${applied.end ? `-to-${applied.end}` : ''}.csv`, toCSV([
    { label: 'Employee ID', get: r => r.employee_no }, { label: 'Employee Name', get: r => r.name }, { label: 'Department', get: r => r.department },
    { label: 'Designation', get: r => r.designation }, { label: 'Branch', get: r => r.branch }, { label: 'Project Site', get: r => r.site },
    { label: 'Grade', get: r => r.grade }, { label: 'Total Missed', get: r => r.total }, { label: 'Approved', get: r => r.approved },
    { label: 'Declined', get: r => r.declined }, { label: 'Pending', get: r => r.pending }, { label: 'Leave Offset', get: r => r.leave_offset },
    { label: 'Paid', get: r => r.paid }, { label: 'Deducted', get: r => r.deducted }, { label: 'Deduction Amt', get: r => r.deduction || '' },
    { label: 'Leave Used', get: r => r.leave_used }, { label: 'Payroll Status', get: r => r.payroll_status }, { label: 'HR Status', get: r => r.hr_status },
  ], rows));
  const kpis = [
    ['Employees', t.employees, Users, 'sky', 'With missed days'], ['Total Missed', t.total, CalendarDays, 'blue', 'Days in period'],
    ['Approved', t.approved, CircleCheckBig, 'green', 'Paid or leave'], ['Declined', t.declined, CircleX, 'red', 'Salary deduction'],
    ['Pending', t.pending, Hourglass, 'amber', 'Awaiting HR review'], ['Leave Offset', t.leave_offset, CalendarCheck, 'indigo', 'Charged to leave'],
    ['Paid', t.paid, Wallet, 'teal', 'No deduction'], ['Deducted', t.deducted, AlertTriangle, 'orange', 'Days deducted'],
    ['Total Deduction', fmtMoney(t.deduction), Banknote, 'rose', 'Payroll impact'],
  ];

  return (
    <div className="mdr mds">
      <div className="mdr-top">
        <div>
          <h2>Missed Days Statement</h2>
          <p>Employee-by-employee missed days summary for selected period</p>
        </div>
        <div className="mdr-top-actions no-print">
          {data.can.export && <button className="mdr-btn" onClick={exportCsv}><Download size={14} /> Export CSV</button>}
          <button className="mdr-btn" onClick={() => window.print()}><Printer size={14} /> Print</button>
        </div>
      </div>
      <p className="print-only mds-print-period">Period: {period}</p>

      <form className="mdr-card mds-filters no-print" onSubmit={run}>
        <div className="mds-card-title"><Filter size={15} /> Report Filters</div>
        <div className="mds-grid">
          <label>From Date<DateInput value={draft.start} onChange={e => set('start', e.target.value)} /></label>
          <label>To Date<DateInput value={draft.end} onChange={e => set('end', e.target.value)} /></label>
          <label>Employee Name / ID<input placeholder="Search..." value={draft.q} onChange={e => set('q', e.target.value)} /></label>
          <label>Department<Select value={draft.department} onChange={e => set('department', e.target.value)}><option value="">All</option>{data.departments.map(d => <option key={d} value={d}>{d}</option>)}</Select></label>
          <label>Branch<Select value={draft.branch} onChange={e => set('branch', e.target.value)}><option value="">All</option>{data.branches.map(d => <option key={d} value={d}>{d}</option>)}</Select></label>
          <label>Project Site<input placeholder="Filter site..." value={draft.site} onChange={e => set('site', e.target.value)} /></label>
          <label>Payroll Status<Select value={draft.payroll_status} onChange={e => set('payroll_status', e.target.value)}><option value="">All</option>{data.payroll_statuses.map(d => <option key={d} value={d}>{d}</option>)}</Select></label>
          <label>HR Decision<Select value={draft.hr_status} onChange={e => set('hr_status', e.target.value)}><option value="">All</option>{data.hr_statuses.map(d => <option key={d} value={d}>{d}</option>)}</Select></label>
        </div>
        <div className="mds-filter-actions">
          <button type="submit" className="mds-run" disabled={loading}><Play size={14} /> {loading ? 'Running…' : 'Run Report'}</button>
          <button type="button" className="mdr-btn" onClick={clear}><RotateCcw size={13} /> Clear Filter</button>
          {error && <span className="mds-error">{error}</span>}
        </div>
      </form>

      <div className="md-kpis mds-kpis" key={`k${tick}`}>
        {kpis.map(([label, value, Ico, tone, note], i) => <Kpi key={label} {...{ label, value, Ico, tone, note, i }} />)}
      </div>

      <div className="mdr-card mdr-table-card">
        <div className="mds-table-head">
          <b>Employee Missed Days Summary — {rows.length} employee(s)</b>
          <div className="mdr-search mds-quick no-print"><Search size={14} /><input placeholder="Quick search..." value={quick} onChange={e => setQuick(e.target.value)} /></div>
        </div>
        <div className="mdr-scroll">
          <table className="mdr-table mds-table mds-frozen" ref={tableRef}>
            <thead>
              <tr>
                <th>S/N</th><th>Employee ID</th><th>Employee Name</th><th>Department</th><th>Designation</th><th>Branch</th><th>Project Site</th><th>Grade</th>
                <th className="c">Total Missed</th><th className="c">Approved</th><th className="c">Declined</th><th className="c">Pending</th><th className="c">Leave Offset</th>
                <th className="c">Paid</th><th className="c">Deducted</th><th className="r">Deduction Amt</th><th>Leave Used</th><th>Payroll Status</th><th>HR Status</th>
                <th className="no-print">Action</th>
              </tr>
            </thead>
            <tbody key={tick}>
              {rows.map((r, i) => (
                <tr key={r.employee_id} className="mdr-row" style={{ animationDelay: `${Math.min(i, 20) * 22}ms` }}>
                  <td>{i + 1}</td><td className="mdr-emp">{r.employee_no}</td><td className="mdr-name">{r.name}</td><td>{r.department}</td>
                  <td className="nowrap">{r.designation || '—'}</td><td className="nowrap">{r.branch || '—'}</td><td className="nowrap">{r.site || '—'}</td><td className="nowrap">{r.grade || '—'}</td>
                  <td className="c b">{num(r.total)}</td><td className="c g">{num(r.approved)}</td><td className="c rd">{num(r.declined)}</td>
                  <td className="c am">{num(r.pending)}</td><td className="c in">{num(r.leave_offset)}</td><td className="c g">{num(r.paid)}</td><td className="c rd">{num(r.deducted)}</td>
                  <td className="r mdr-ded">{r.deduction ? <span className="neg">{fmtMoney(r.deduction)}</span> : '—'}</td>
                  <td className="nowrap">{r.leave_used || '—'}</td>
                  <td><Chip tone={PAY_TONE[r.payroll_status]}>{r.payroll_status}</Chip></td>
                  <td><Chip tone={HR_TONE[r.hr_status]}>{r.hr_status}</Chip></td>
                  <td className="no-print"><button className="mds-view" onClick={() => setViewing(r.employee_id)}><Eye size={13} /> View</button></td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={20} className="mdr-empty">No missed days for the selected filters.</td></tr>}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr>
                  <td>TOTALS</td><td /><td /><td colSpan={5} />
                  {['total', 'approved', 'declined', 'pending', 'leave_offset', 'paid', 'deducted'].map(k => <td key={k} className="c">{fmtNumber(sum(k))}</td>)}
                  <td className="r">{fmtMoney(sum('deduction'))}</td>
                  <td colSpan={4} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="mdr-foot">
          <span>Period: <b>{period}</b> · Showing {rows.length} of {data.rows.length} employee(s)</span>
          <span>Total Deduction: <strong>{fmtMoney(sum('deduction'))}</strong></span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- one employee's missed days history (View)
const TABS = ['Overview', 'Missed Days Details', 'Leave Offset History', 'Lateness History', 'Payroll Impact', 'Year-by-Year Entitlement', 'Management Actions'];

function EmployeeHistory({ id, period, onBack }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(TABS[0]);

  useEffect(() => {
    hr.mdStatementEmployee(id, { start: period.start, end: period.end }).then(setD).catch(e => setError(e.message));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <div className="mdr"><button className="mdr-btn" onClick={onBack}><ArrowLeft size={14} /> Back to Report</button><div className="mdr-card mdr-empty">{error}</div></div>;
  if (!d) return <Loader title="Loading employee history…" subtitle="Fetching the employee's records" />;
  const p = d.profile;
  const k = d.kpis;
  const csv = (name, cols, rows) => download(`${p.employee_no}-${name}.csv`, toCSV(cols, rows));
  const printing = !!(period.start || period.end);

  return (
    <div className="mdr mds mds-hist">
      <div className="no-print"><button className="mdr-btn mds-back" onClick={onBack}><ArrowLeft size={14} /> Back to Report</button></div>
      <div className="mdr-top">
        <div>
          <h2>{p.name} — Employee Missed Days History</h2>
          <p>{[p.employee_no, p.department, p.designation].filter(Boolean).join(' | ')}{printing ? ` · ${period.start || 'start'} to ${period.end || 'date'}` : ''}</p>
        </div>
        <div className="mdr-top-actions no-print">
          <button className="mdr-btn" onClick={() => window.print()}><Printer size={14} /> Print Statement</button>
        </div>
      </div>

      <div className="mds-tabs no-print" role="tablist">
        {TABS.map(t => <button key={t} type="button" role="tab" className={t === tab ? 'active' : ''} aria-selected={t === tab} onClick={() => setTab(t)}>{t}</button>)}
      </div>

      <div key={tab} className="mds-pane">
        {tab === 'Overview' && (
          <>
            <div className="mds-overview">
              <div className="mdr-card mds-profile">
                <div className="mds-section">Employee Profile</div>
                <dl>
                  {[['Employee Name', p.name], ['Employee ID', p.employee_no], ['Department', p.department], ['Designation', p.designation],
                    ['Branch', p.branch], ['Project Site', p.site], ['Grade / Level', p.grade], ['Employment Type', p.employment_type],
                    ['Supervisor', p.supervisor], ['Manager', p.manager], ['Date Joined', p.joined], ['Status', p.status && p.status[0].toUpperCase() + p.status.slice(1)]]
                    .map(([l, v]) => <div key={l}><dt>{l}</dt><dd>{v || '—'}</dd></div>)}
                </dl>
              </div>
              <div className="mdr-card mds-profile">
                <div className="mds-section">Leave Balances (Current)</div>
                <div className="mds-bals">
                  {['Casual', 'Sick', 'Annual'].map(t => {
                    const b = d.balances[t] || {};
                    return (
                      <div key={t} className={`mds-bal ${b.available > 0 ? 'ok' : 'zero'}`}>
                        <small>{t} Leave Balance</small><b>{num(b.available)} days</b>
                        <span>{num(b.used)} used of {num(b.entitled)}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="mds-note">
                  {d.last_action
                    ? <>Recent Actions: {d.last_action.by} took action on {String(d.last_action.on || '').slice(0, 10)}{d.last_action.to ? ` (${d.last_action.to})` : ''}. </>
                    : 'No review actions recorded yet. '}
                  Current HR Status: <b>{k.hr_status}</b> · Payroll Status: <b>{k.payroll_status}</b>
                </div>
              </div>
            </div>
            <div className="md-kpis mds-kpis">
              {[['Total Missed Days', k.total, CalendarDays, 'blue', 'In period'], ['Approved / Paid', k.approved, CircleCheckBig, 'green', 'No deduction'],
                ['Declined / Deducted', k.declined, CircleX, 'red', 'Salary deduction'], ['Leave Offset Days', k.leave_offset, CalendarCheck, 'indigo', 'Charged to leave'],
                ['Pending Review', k.pending, Hourglass, 'amber', 'Awaiting HR'], ['Total Deduction', fmtMoney(k.deduction), Banknote, 'rose', 'Payroll impact'],
                ['Lateness Cases', k.lateness, Clock, 'orange', 'Late clock-ins'], ['Minutes Late', k.late_minutes, AlertTriangle, 'teal', 'Total minutes']]
                .map(([label, value, Ico, tone, note], i) => <Kpi key={label} {...{ label, value, Ico, tone, note, i }} />)}
            </div>
          </>
        )}

        {tab === 'Missed Days Details' && (
          <Table
            onExport={() => csv('missed-days', [
              { label: 'MD ID', get: r => r.reference }, { label: 'Date', get: r => r.date }, { label: 'Day', get: r => r.weekday },
              { label: 'Shift Expected', get: r => times12(r.shift) }, { label: 'Clock-in', get: r => t12(r.clock_in) }, { label: 'Clock-out', get: r => t12(r.clock_out) },
              { label: 'Source', get: r => r.source }, { label: 'Reason', get: r => r.reason }, { label: 'Explanation', get: r => r.explanation },
              { label: 'Status', get: r => r.status }, { label: 'Paid?', get: r => (r.paid ? 'Yes' : 'No') }, { label: 'Deducted?', get: r => (r.deducted ? 'Yes' : 'No') },
              { label: 'Daily Rate', get: r => r.daily_rate }, { label: 'Deduction', get: r => r.deduction || '' }, { label: 'Payroll Period', get: r => r.payroll_period },
              { label: 'Posting Status', get: r => r.posting }, { label: 'Action By', get: r => r.action_by }, { label: 'Action Date', get: r => r.action_date },
            ], d.details)}
            head={['MD ID', 'Date', 'Day', 'Shift Expected', 'Clock-in', 'Clock-out', 'Source', 'Reason', 'Explanation', 'Leave', 'Status', 'Paid?', 'Deducted?', 'Daily Rate', 'Deduction', 'Payroll Period', 'Posting Status', 'Action By', 'Action Date']}
            rows={d.details}
            row={r => [
              <span className="mdr-id">{r.reference}</span>, <span className="nowrap">{r.date}</span>, r.weekday, <span className="nowrap">{times12(r.shift)}</span>,
              t12(r.clock_in) || '—', t12(r.clock_out) || '—', r.source, <span className="mds-wrap">{r.reason || '—'}</span>, <span className="mds-wrap">{r.explanation || '—'}</span>,
              r.leave_type ? `${r.leave_type} Leave` : '—', <Chip tone={STATUS_TONE[r.status]}>{r.status}</Chip>,
              <YesNo v={r.paid} />, <YesNo v={r.deducted} bad />, <span className="nowrap">{fmtMoney(r.daily_rate)}</span>,
              <span className="mdr-ded">{r.deduction ? <span className="neg">{fmtMoney(r.deduction)}</span> : '—'}</span>, <span className="nowrap">{r.payroll_period}</span>,
              <Chip tone={PAY_TONE[r.posting]}>{r.posting}</Chip>, r.action_by || '—', r.action_date || '—',
            ]}
            foot={d.details.length ? [`TOTALS — ${d.details.length} record(s)`, 14, money(d.details.reduce((s, r) => s + (r.deduction || 0), 0)), 4] : null}
          />
        )}

        {tab === 'Leave Offset History' && (
          <Table
            note="Missed days settled against the employee's leave balance instead of a salary deduction."
            onExport={() => csv('leave-offset', [
              { label: 'MD ID', get: r => r.reference }, { label: 'Date', get: r => r.date }, { label: 'Days', get: r => r.days }, { label: 'Leave Type', get: r => r.leave_type },
              { label: 'Leave ID', get: r => r.leave_ref }, { label: 'Leave Status', get: r => r.leave_status }, { label: 'Note', get: r => r.note },
              { label: 'Assigned By', get: r => r.by }, { label: 'Assigned On', get: r => r.on },
            ], d.offsets)}
            head={['MD ID', 'Date', 'Day', 'Days', 'Leave Type', 'Leave ID', 'Leave Status', 'Note', 'Assigned By', 'Assigned On']}
            rows={d.offsets}
            empty="No missed days have been offset against leave."
            row={r => [<span className="mdr-id">{r.reference}</span>, <span className="nowrap">{r.date}</span>, r.weekday, num(r.days),
              <Chip tone="indigo">{r.leave_type ? `${r.leave_type} Leave` : null}</Chip>, r.leave_ref || '—', r.leave_status ? <span className="cap">{r.leave_status}</span> : '—',
              <span className="mds-wrap">{r.note || '—'}</span>, r.by || '—', r.on || '—']}
          />
        )}

        {tab === 'Lateness History' && (
          <>
            <div className="md-kpis mds-kpis">
              {[['Total Cases', d.lateness.length, 'orange', 'Late clock-ins'], ['Total Mins Late', `${fmtNumber(k.late_minutes)} min`, 'amber', 'Beyond shift start'],
                ['Total Hrs Late', `${(k.late_minutes / 60).toFixed(2)} hrs`, 'rose', 'In period']]
                .map(([label, value, tone, note], i) => <Kpi key={label} {...{ label, value, Ico: Clock, tone, note, i }} />)}
            </div>
            <Table
              onExport={() => csv('lateness', [
                { label: 'Date', get: r => r.date }, { label: 'Day', get: r => r.weekday }, { label: 'Expected In', get: r => r.expected }, { label: 'Actual In', get: r => r.actual },
                { label: 'Mins Late', get: r => r.minutes }, { label: 'Hrs Late', get: r => r.hours }, { label: 'Source', get: r => r.source }, { label: 'Reason', get: r => r.reason },
              ], d.lateness)}
              head={['Date', 'Day', 'Expected In', 'Actual In', 'Mins Late', 'Hrs Late', 'Source', 'Reason / Remark']}
              rows={d.lateness}
              empty="No late clock-ins recorded in this period."
              row={r => [<span className="nowrap">{r.date}</span>, r.weekday, r.expected, <b className="neg">{r.actual}</b>, <b>{r.minutes}</b>, r.hours.toFixed(2), r.source || '—', r.reason || '—']}
            />
          </>
        )}

        {tab === 'Payroll Impact' && (
          <>
            <div className="md-kpis mds-kpis">
              {[['Total Deducted', fmtMoney(k.deduction), 'rose', 'Missed days deductions'], ['Payroll Months', d.payroll.length, 'blue', 'Months with missed days'],
                ['Posted Months', d.payroll.filter(m => m.posting === 'Posted').length, 'green', 'Sent to payroll'],
                ['Pending Posting', d.payroll.filter(m => m.posting === 'Pending Posting').length, 'amber', 'Awaiting payroll run']]
                .map(([label, value, tone, note], i) => <Kpi key={label} {...{ label, value, Ico: Banknote, tone, note, i }} />)}
            </div>
            <Table
              onExport={() => csv('payroll-impact', [
                { label: 'Month', get: r => r.month }, { label: 'Year', get: r => r.year }, { label: 'Days Deducted', get: r => r.deducted_days }, { label: 'Daily Rate', get: r => r.daily_rate },
                { label: 'Deduction', get: r => r.deduction }, { label: 'Paid Days', get: r => r.paid_days }, { label: 'Leave Offset', get: r => r.leave_offset },
                { label: 'Pending', get: r => r.pending }, { label: 'Batch', get: r => r.batch }, { label: 'Posting Status', get: r => r.posting },
              ], d.payroll)}
              head={['Month', 'Year', 'Days Deducted', 'Daily Rate', 'Missed Deduction', 'Paid Missed', 'Leave Offset', 'Pending', 'Batch', 'Posting Status', 'Posted Date']}
              rows={d.payroll}
              row={r => [r.month, r.year, num(r.deducted_days), <span className="nowrap">{fmtMoney(r.daily_rate)}</span>,
                <span className="mdr-ded">{r.deduction ? <span className="neg">{fmtMoney(r.deduction)}</span> : '—'}</span>, num(r.paid_days), num(r.leave_offset), num(r.pending),
                <span className="mdr-id">{r.batch}</span>, <Chip tone={PAY_TONE[r.posting]}>{r.posting}</Chip>, r.posted_on || '—']}
              foot={d.payroll.length ? ['TOTAL DEDUCTION', 4, money(k.deduction), 6] : null}
            />
          </>
        )}

        {tab === 'Year-by-Year Entitlement' && (
          <Table
            note="Entitlement is shown for each year from the leave entitlement in effect for the employee — not the current entitlement applied backwards."
            onExport={() => csv('entitlement', [
              { label: 'Year', get: r => r.year }, { label: 'Casual', get: r => r.casual }, { label: 'Sick', get: r => r.sick }, { label: 'Annual', get: r => r.annual },
              { label: 'Other', get: r => r.other }, { label: 'Total Entitlement', get: r => r.total }, { label: 'Leave Used', get: r => r.used },
              { label: 'MD Offset', get: r => r.md_offset }, { label: 'Excess', get: r => r.excess }, { label: 'Deducted', get: r => r.deducted }, { label: 'Payroll Impact', get: r => r.impact },
            ], d.entitlement)}
            head={['Year', 'Grade / Level', 'Designation', 'Emp. Type', 'Casual', 'Sick', 'Annual', 'Other', 'Total Entit.', 'Leave Used', 'MD Offset', 'Excess', 'Deducted', 'Payroll Impact']}
            rows={d.entitlement}
            row={r => [<b>{r.year}</b>, p.grade || '—', p.designation || '—', p.employment_type || '—', num(r.casual), num(r.sick), num(r.annual), num(r.other),
              <b>{num(r.total)}</b>, num(r.used), num(r.md_offset), r.excess ? <b className="neg">{num(r.excess)}</b> : '—', r.deducted ? <b className="neg">{num(r.deducted)}</b> : '—',
              <Chip tone={r.deducted ? 'red' : 'green'}>{r.impact}</Chip>]}
          />
        )}

        {tab === 'Management Actions' && (
          <div className="mds-timeline">
            {d.actions.map((a, i) => (
              <div key={a.id} className="mdr-card mds-event" style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}>
                <div className="mds-event-head">
                  <b>{a.to || a.action} <span>— Missed Day {a.reference}</span></b>
                  <small>{String(a.on || '').replace('T', ' ').slice(0, 16)}</small>
                </div>
                {a.from && <div className="mds-flow"><Chip tone={STATUS_TONE[a.from]}>{a.from}</Chip><span>→</span><Chip tone={STATUS_TONE[a.to]}>{a.to}</Chip></div>}
                {!a.from && <div className="mds-wrap">{a.summary}</div>}
                <div className="mds-by">By: {a.by}</div>
                {a.note && <q>{a.note}</q>}
              </div>
            ))}
            {!d.actions.length && <div className="mdr-card mdr-empty">No management actions recorded for this employee's missed days.</div>}
          </div>
        )}
      </div>
    </div>
  );
}
